import React, { useState, useRef, useEffect } from 'react';
import { X, Send, MessageCircle } from 'lucide-react';
import { createTicket, addMessageToTicket } from '../../services/ticketService';
import { useAuth } from '../../context/AuthContext';

export default function CustomerTicketModal({ isOpen, onClose, animal, existingTicket }) {
  const { userProfile } = useAuth();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when ticket messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [existingTicket?.messages]);

  if (!isOpen) return null;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (existingTicket) {
        // Talebe cevap yaz
        await addMessageToTicket(existingTicket.id, {
          senderRole: 'customer',
          senderName: `${userProfile.firstName} ${userProfile.lastName}`,
          text: message.trim()
        });
      } else {
        // Yeni Talep Oluştur
        const newTicket = {
          animalId: animal.id,
          animalTagNo: animal.tagNo,
          branchId: animal.branch, // Şubeye atanacak!
          customerPhone: userProfile.phone,
          customerName: `${userProfile.firstName} ${userProfile.lastName}`,
          messages: [{
            senderRole: 'customer',
            senderName: `${userProfile.firstName} ${userProfile.lastName}`,
            text: message.trim(),
            createdAt: new Date().toISOString()
          }]
        };
        await createTicket(newTicket);
      }
      setMessage('');
    } catch (err) {
      alert("Mesaj gönderilemedi, lütfen tekrar deneyin.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-md">
      <div className="bg-navy-900 border border-navy-700 w-full max-w-lg rounded-2xl shadow-2xl flex flex-col h-[600px] max-h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-navy-700/50 bg-navy-800/40 rounded-t-2xl">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
               <MessageCircle className="w-5 h-5 text-forest-400" />
               Destek Talebi
            </h3>
            <p className="text-xs text-navy-400 mt-0.5">
               KÜPE: <span className="text-white font-semibold">{animal?.tagNo}</span> ({animal?.branchName || 'Sistem'})
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Chat Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-navy-950/30">
          {!existingTicket ? (
            <div className="flex flex-col items-center justify-center h-full text-center opacity-70">
              <MessageCircle className="w-12 h-12 text-navy-500 mb-3" />
              <p className="text-navy-300 font-medium">Bu hisse için sorularınızı şube yetkilisine iletebilirsiniz.</p>
              <p className="text-xs text-navy-500 mt-1">Yetkilimiz en kısa sürede size dönüş yapacaktır.</p>
            </div>
          ) : (
            existingTicket.messages?.map((msg, index) => {
              const isCust = msg.senderRole === 'customer';
              return (
                <div key={index} className={`flex ${isCust ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    isCust 
                      ? 'bg-forest-600/90 text-white rounded-br-sm' 
                      : 'bg-navy-800 text-navy-100 rounded-bl-sm border border-navy-700'
                  }`}>
                    {!isCust && <div className="text-[10px] font-bold text-gold-400 mb-1">Şube Yöneticisi</div>}
                    <p className="text-sm">{msg.text}</p>
                    <span className="text-[10px] opacity-60 mt-1 block text-right">
                       {new Date(msg.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute:'2-digit' })}
                    </span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <form onSubmit={handleSend} className="p-4 border-t border-navy-700/50 bg-navy-900 rounded-b-2xl">
           <div className="flex items-end gap-2">
             <textarea
               value={message}
               onChange={(e) => setMessage(e.target.value)}
               placeholder="Mesajınızı yazın..."
               className="flex-1 bg-navy-950 border border-navy-700 rounded-xl px-4 py-3 text-sm text-white placeholder-navy-500 focus:outline-none focus:border-forest-500 focus:ring-1 focus:ring-forest-500 resize-none"
               rows={1}
               onKeyDown={(e) => {
                 if (e.key === 'Enter' && !e.shiftKey) {
                   e.preventDefault();
                   handleSend(e);
                 }
               }}
             />
             <button
               type="submit"
               disabled={!message.trim() || isSubmitting}
               className="p-3 bg-forest-600 hover:bg-forest-500 disabled:opacity-50 disabled:hover:bg-forest-600 text-white rounded-xl transition-all shadow-lg flex-shrink-0"
             >
               <Send className="w-5 h-5" />
             </button>
           </div>
        </form>
      </div>
    </div>
  );
}
